import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  File,
  FileText,
  Image,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  Download,
  RefreshCw
} from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { id: "passport", name: "Pasaporte", description: "Pasaporte vigente" },
  { id: "id_card", name: "Identificación (INE/ID)", description: "Identificación oficial vigente" },
  { id: "photo", name: "Fotografía", description: "Foto tipo visa fondo blanco" },
  { id: "employment_letter", name: "Carta de Trabajo", description: "Carta de empleo actual" },
  { id: "pay_stubs", name: "Recibos de Sueldo", description: "Últimos 3 recibos de nómina" },
  { id: "work_contract", name: "Contrato Laboral", description: "Si lo tienes disponible" },
  { id: "bank_statements", name: "Estados de Cuenta", description: "Últimos 3-6 meses" },
  { id: "previous_visa", name: "Visa Anterior", description: "Visa americana previa" },
  { id: "income_proof", name: "Comprobante de Ingresos", description: "Recibos de nómina" },
  { id: "marriage_certificate", name: "Acta de Matrimonio", description: "Si aplica" },
  { id: "birth_certificate", name: "Acta de Nacimiento", description: "Acta de nacimiento" },
  { id: "address_proof", name: "Comprobante de Domicilio", description: "No mayor a 3 meses" },
  { id: "property_deed", name: "Escrituras de Propiedad", description: "Si aplica" },
  { id: "tax_return", name: "Declaración de Impuestos", description: "Última declaración" },
  { id: "travel_itinerary", name: "Itinerario de Viaje", description: "Plan de viaje" },
  { id: "invitation_letter", name: "Carta de Invitación", description: "Si aplica" },
  { id: "other", name: "Otro Documento", description: "Documento adicional" }
];

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".heic"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const FileIcon = ({ mimeType }) => {
  if (mimeType?.startsWith("image/")) {
    return <Image className="w-6 h-6 text-blue-500" />;
  }
  if (mimeType === "application/pdf") {
    return <FileText className="w-6 h-6 text-red-500" />;
  }
  return <File className="w-6 h-6 text-gray-500" />;
};

const DocumentUploader = ({ 
  onUploadSuccess, 
  caseId = null, 
  stepName = null,
  preselectedType = null,
  compact = false,
  personId = null,
  personName = null
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [documentType, setDocumentType] = useState(preselectedType || "");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const ext = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Formato no permitido. Use: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Archivo muy grande. Máximo: ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return false;
    }
    return true;
  };

  const handleFile = (file) => {
    console.log("File selected:", file.name, file.type, file.size);
    if (!validateFile(file)) return;
    
    setSelectedFile(file);
    console.log("File set successfully:", file.name);
    
    // Generate preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Selecciona un archivo");
      return;
    }
    if (!documentType) {
      toast.error("Selecciona el tipo de documento");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("document_type", documentType);
    if (caseId) formData.append("case_id", caseId);
    if (stepName) formData.append("step_name", stepName);
    if (notes) formData.append("notes", notes);
    if (personId) formData.append("person_id", personId);
    if (personName) formData.append("person_name", personName);

    // Retry logic for file uploads
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        const response = await axios.post(`${API}/documents/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000, // 2 minutes for large files
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });

        toast.success("Documento subido exitosamente");
        
        // Reset form
        setSelectedFile(null);
        setPreview(null);
        setDocumentType(preselectedType || "");
        setNotes("");
        
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
        break; // Success, exit retry loop
        
      } catch (error) {
        attempts++;
        console.error(`Upload attempt ${attempts} failed:`, error);
        
        if (attempts >= maxAttempts) {
          // Final failure - show appropriate error message
          let errorMessage = "Error al subir documento";
          
          if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            errorMessage = "Tiempo de espera agotado. El archivo puede ser muy grande. Intenta de nuevo.";
          } else if (error.code === 'ERR_NETWORK') {
            errorMessage = "Error de conexión. Verifica tu internet e intenta de nuevo.";
          } else if (error.response?.status === 413) {
            errorMessage = "El archivo es demasiado grande. Máximo 10MB.";
          } else if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          }
          
          toast.error(errorMessage);
        } else {
          // Wait before retrying (exponential backoff)
          setUploadProgress(0);
          toast.info(`Reintentando... (${attempts}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
        }
      }
    }
    
    setUploading(false);
    setUploadProgress(0);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-navy-100 ${compact ? 'p-4' : 'p-6'}`}>
      {!compact && (
        <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
          Subir Documento
        </h3>
      )}

      {/* Document Type Selector */}
      {!preselectedType && (
        <div className="mb-4">
          <label className="text-sm font-medium text-navy-700 mb-2 block">
            Tipo de Documento *
          </label>
          <Select value={documentType} onValueChange={setDocumentType}>
            <SelectTrigger data-testid="document-type-select">
              <SelectValue placeholder="Selecciona el tipo de documento" />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  <div className="flex flex-col">
                    <span>{type.name}</span>
                    <span className="text-xs text-navy-400">{type.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Drop Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
          ${dragActive ? 'border-emerald-500 bg-emerald-50' : 'border-navy-200 hover:border-navy-300'}
          ${selectedFile ? 'bg-navy-50' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="file-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,image/*"
          onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
        />

        <AnimatePresence mode="wait">
          {selectedFile ? (
            <motion.div
              key="file-selected"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="space-y-3"
            >
              {preview ? (
                <img 
                  src={preview} 
                  alt="Preview" 
                  className="w-24 h-24 object-cover rounded-lg mx-auto border border-navy-200"
                />
              ) : (
                <FileIcon mimeType={selectedFile.type} />
              )}
              <div>
                <p className="font-medium text-navy-900 truncate max-w-[200px] mx-auto">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-navy-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFile();
                }}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1" />
                Quitar archivo
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="no-file"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              <div className="w-12 h-12 rounded-full bg-navy-100 flex items-center justify-center mx-auto">
                <Upload className="w-6 h-6 text-navy-500" />
              </div>
              <div>
                <p className="font-medium text-navy-700">
                  Arrastra tu archivo aquí
                </p>
                <p className="text-sm text-navy-500">
                  o haz clic para seleccionar
                </p>
              </div>
              <p className="text-xs text-navy-400">
                PDF, JPG, PNG • Máximo 10MB
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Progress Bar */}
      {uploading && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-600">Subiendo...</span>
            <span className="text-sm font-medium text-navy-900">{uploadProgress}%</span>
          </div>
          <div className="h-2 bg-navy-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-emerald-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Notes (optional) */}
      {!compact && (
        <div className="mt-4">
          <label className="text-sm font-medium text-navy-700 mb-2 block">
            Notas (opcional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Agrega notas sobre este documento..."
            className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm resize-none h-20 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          />
        </div>
      )}

      {/* Validation Message */}
      {(!documentType || !selectedFile) && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700 font-medium">
            {!documentType && !selectedFile 
              ? "Primero selecciona el tipo de documento y luego sube un archivo"
              : !documentType 
              ? "⚠️ Primero selecciona el tipo de documento arriba"
              : "Selecciona un archivo para continuar"
            }
          </p>
        </div>
      )}

      {/* Upload Button */}
      <Button
        onClick={() => {
          if (!documentType) {
            toast.error("Primero selecciona el tipo de documento");
            return;
          }
          if (!selectedFile) {
            toast.error("Selecciona un archivo para subir");
            return;
          }
          handleUpload();
        }}
        disabled={uploading}
        className={`w-full mt-4 ${!selectedFile || !documentType ? 'bg-gray-400 hover:bg-gray-500' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
        data-testid="upload-document-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Subiendo...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            Subir Documento
          </>
        )}
      </Button>
    </div>
  );
};

export default DocumentUploader;
